import React, { useEffect, useState } from 'react';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getRepositories, browseRepositories, createAccessRequest, createAccessRequestByName, updateRepoSettings, toggleFavorite, syncRepositories, createRepository } from '../services/api';
import { formatAccessExpiry } from '../utils/formatAccessExpiry';
import './Repositories.css';
import './AdminPages.css';

function serviceDeskTicketError(value) {
  const ticket = String(value || '').trim().replace(/^#/, '');
  if (!ticket) return 'Enter the service desk request number.';
  if (!/^(?:[A-Fa-f0-9]{6}|[A-Fa-f0-9]{24})$/.test(ticket)) {
    return 'Enter the service desk ticket code from the email, such as A1B2C3, or the full request id.';
  }
  return '';
}

const Repositories = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [repositories, setRepositories] = useState([]);
  const [allRepositories, setAllRepositories] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const [filter, setFilter] = useState('all'); // all, private, public
  const [searchTerm, setSearchTerm] = useState('');
  const [requestModalRepo, setRequestModalRepo] = useState(null);
  const [requestPermission, setRequestPermission] = useState('read');
  const [requestReason, setRequestReason] = useState('');
  const [serviceDeskRequestNumber, setServiceDeskRequestNumber] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [showRequestByNameModal, setShowRequestByNameModal] = useState(false);
  const [requestByNameFullName, setRequestByNameFullName] = useState('');
  const [requestByNamePermission, setRequestByNamePermission] = useState('read');
  const [requestByNameReason, setRequestByNameReason] = useState('');
  const [requestByNameTicket, setRequestByNameTicket] = useState('');
  const [requestByNameSubmitting, setRequestByNameSubmitting] = useState(false);
  const [requestByNameError, setRequestByNameError] = useState('');
  const [requestByNameSuccess, setRequestByNameSuccess] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [requestGitHubUsername, setRequestGitHubUsername] = useState('');
  const [syncingRepos, setSyncingRepos] = useState(false);
  const [repoNotice, setRepoNotice] = useState('');
  const [repoNoticeError, setRepoNoticeError] = useState('');
  const [showCreateRepo, setShowCreateRepo] = useState(false);
  const [creatingRepo, setCreatingRepo] = useState(false);
  const [createRepoError, setCreateRepoError] = useState('');
  const [createRepoForm, setCreateRepoForm] = useState({
    name: '',
    description: '',
    org: '',
    private: true
  });
  const canManageRepos = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    if (user?.favoriteRepos) {
      setFavorites(new Set(user.favoriteRepos.map((id) => id.toString())));
    }
    setRequestGitHubUsername(user?.githubUsername || (user?.accountType === 'github' ? user?.username : '') || '');
  }, [user]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }

    if (user) {
      fetchRepositories();
    }
  }, [user, loading, navigate]);

  const fetchRepositories = async () => {
    try {
      setLoadingRepos(true);
      const response = await getRepositories();
      const data = response.data || [];
      setAllRepositories(data);
      setRepositories(data);
    } catch (error) {
      console.error('Error fetching repositories:', error);
      try {
        const browseRes = await browseRepositories();
        const data = browseRes.data || [];
        setAllRepositories(data);
        setRepositories(data);
      } catch (browseErr) {
        console.error('Error browsing repositories:', browseErr);
      }
    } finally {
      setLoadingRepos(false);
    }
  };

  useEffect(() => {
    // Filter repositories based on search term and filter
    let filtered = allRepositories;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(repo =>
        repo.name.toLowerCase().includes(term) ||
        repo.fullName.toLowerCase().includes(term) ||
        repo.owner.toLowerCase().includes(term) ||
        (repo.description && repo.description.toLowerCase().includes(term))
      );
    }

    // Apply type filter
    if (filter === 'private') {
      filtered = filtered.filter(repo => repo.private);
    } else if (filter === 'public') {
      filtered = filtered.filter(repo => !repo.private);
    }

    if (showFavoritesOnly) {
      filtered = filtered.filter(repo => favorites.has(repo._id));
    }

    setRepositories(filtered);
  }, [searchTerm, filter, allRepositories, showFavoritesOnly, favorites]);

  const repoPages = usePagination(repositories, 20, `${searchTerm}|${filter}|${showFavoritesOnly}`);

  const handleToggleFavorite = async (repoId) => {
    try {
      await toggleFavorite(repoId);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (next.has(repoId)) next.delete(repoId);
        else next.add(repoId);
        return next;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const openRequestModal = (repo) => {
    setRequestModalRepo(repo);
    setRequestPermission('read');
    setRequestReason('');
    setServiceDeskRequestNumber('');
    setRequestError('');
    setRequestSuccess(false);
    setRequestGitHubUsername(user?.githubUsername || (user?.accountType === 'github' ? user?.username : '') || '');
  };

  const closeRequestModal = () => {
    setRequestModalRepo(null);
    setRequestSubmitting(false);
    setRequestError('');
  };

  const resolveRequestedFor = () => {
    const gh = (requestGitHubUsername || '').trim();
    if (gh) return gh;
    return user?.githubUsername || (user?.accountType === 'github' ? user?.username : '') || '';
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    if (!requestModalRepo || !user) return;
    const requestedFor = resolveRequestedFor();
    if (!requestedFor) {
      setRequestError('Enter your GitHub username (the account that should receive access).');
      return;
    }
    const ticketError = serviceDeskTicketError(serviceDeskRequestNumber);
    if (ticketError) {
      setRequestError(ticketError);
      return;
    }
    if (!requestReason.trim() || requestReason.trim().length < 10) {
      setRequestError('Please provide a business justification (at least 10 characters).');
      return;
    }
    setRequestSubmitting(true);
    setRequestError('');
    try {
      await createAccessRequest(
        requestModalRepo._id,
        requestedFor,
        requestPermission,
        requestReason,
        serviceDeskRequestNumber.trim()
      );
      setRequestSuccess(true);
      setTimeout(() => closeRequestModal(), 1500);
    } catch (err) {
      setRequestError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const closeRequestByNameModal = () => {
    setShowRequestByNameModal(false);
    setRequestByNameFullName('');
    setRequestByNamePermission('read');
    setRequestByNameReason('');
    setRequestByNameTicket('');
    setRequestByNameSubmitting(false);
    setRequestByNameError('');
    setRequestByNameSuccess(false);
  };

  const handleRequestByName = async (e) => {
    e.preventDefault();
    if (!user) return;
    const requestedFor = resolveRequestedFor();
    if (!requestedFor) {
      setRequestByNameError('Enter your GitHub username (the account that should receive access).');
      return;
    }
    if (!requestByNameFullName.trim()) {
      setRequestByNameError('Enter the repository name (owner/repo).');
      return;
    }
    const ticketError = serviceDeskTicketError(requestByNameTicket);
    if (ticketError) {
      setRequestByNameError(ticketError);
      return;
    }
    if (!requestByNameReason.trim() || requestByNameReason.trim().length < 10) {
      setRequestByNameError('Please provide a business justification (at least 10 characters).');
      return;
    }
    setRequestByNameSubmitting(true);
    setRequestByNameError('');
    try {
      await createAccessRequestByName(
        requestByNameFullName.trim(),
        requestByNamePermission,
        requestByNameReason,
        requestedFor,
        requestByNameTicket.trim()
      );
      setRequestByNameSuccess(true);
      setTimeout(() => closeRequestByNameModal(), 2000);
    } catch (err) {
      setRequestByNameError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setRequestByNameSubmitting(false);
    }
  };

  const getApprovalHintText = (repo) => {
    if (repo?.requiresMultiApproval) {
      return 'Multi-step approval: manager or repo owner first, then a portal admin grants access.';
    }
    return 'A manager, repo owner, or portal admin can approve your request and grant access.';
  };

  const handleSyncFromGitHub = async () => {
    try {
      setSyncingRepos(true);
      setRepoNotice('');
      setRepoNoticeError('');
      const res = await syncRepositories();
      setRepoNotice(res.data.message || 'Repositories synced from GitHub');
      await fetchRepositories();
    } catch (err) {
      setRepoNoticeError(err.response?.data?.error || 'Failed to sync repositories from GitHub');
    } finally {
      setSyncingRepos(false);
    }
  };

  const handleCreateRepository = async (e) => {
    e.preventDefault();
    try {
      setCreatingRepo(true);
      setCreateRepoError('');
      const res = await createRepository({
        name: createRepoForm.name.trim(),
        description: createRepoForm.description.trim(),
        org: createRepoForm.org.trim(),
        private: createRepoForm.private
      });
      setRepoNotice(res.data.message || 'Repository created on GitHub');
      setShowCreateRepo(false);
      setCreateRepoForm({ name: '', description: '', org: '', private: true });
      await fetchRepositories();
    } catch (err) {
      setCreateRepoError(err.response?.data?.error || 'Failed to create repository');
    } finally {
      setCreatingRepo(false);
    }
  };

  const toggleMultiApproval = async (repo) => {
    try {
      await updateRepoSettings(repo._id, { requiresMultiApproval: !repo.requiresMultiApproval });
      fetchRepositories();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update repository settings');
    }
  };

  if (loading) {
    return (
      <div className="repositories">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="repositories">
      <div className="page-header">
        <div>
          <h1 className="page-title">Repositories</h1>
          {user?.role === 'admin' && (
            <p className="admin-notice">👑 Admin View: You can see and manage all repositories</p>
          )}
          {user?.role !== 'admin' && (
            <p className="admin-notice browse-notice">
              Browse registered repositories and click <strong>Request access</strong>, or use <strong>Request by repo name</strong> if yours is not listed.
            </p>
          )}
        </div>
        <div className="header-controls">
          {canManageRepos && (
            <>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setShowCreateRepo(true);
                  setCreateRepoError('');
                }}
              >
                Create repository
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleSyncFromGitHub}
                disabled={syncingRepos}
              >
                {syncingRepos ? 'Syncing…' : 'Sync from GitHub'}
              </button>
            </>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => {
              setShowRequestByNameModal(true);
              setRequestByNameError('');
              setRequestByNameSuccess(false);
            }}
          >
            Request by repo name
          </button>
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search repositories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({allRepositories.length})
          </button>
          <button 
            className={`filter-btn ${filter === 'private' ? 'active' : ''}`}
            onClick={() => setFilter('private')}
          >
            Private ({allRepositories.filter(r => r.private).length})
          </button>
          <button 
            className={`filter-btn ${filter === 'public' ? 'active' : ''}`}
            onClick={() => setFilter('public')}
          >
            Public ({allRepositories.filter(r => !r.private).length})
          </button>
          <button
            className={`filter-btn ${showFavoritesOnly ? 'active' : ''}`}
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          >
            ★ Favorites
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={fetchRepositories}
            title="Refresh repositories"
          >
            🔄 Refresh
          </button>
          </div>
        </div>
      </div>

      {repoNotice && <div className="alert alert-success">{repoNotice}</div>}
      {repoNoticeError && <div className="alert alert-error">{repoNoticeError}</div>}

      <div className="repositories-list">
        {loadingRepos ? (
          <div className="empty-state">
            <div className="spinner"></div>
            <p>Loading repositories…</p>
          </div>
        ) : repositories.length === 0 ? (
          <div className="empty-state">
            <p>No repositories found in the portal catalog.</p>
            <p>If you know the repository name, you can still request access.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowRequestByNameModal(true)}
            >
              Request access by repo name
            </button>
          </div>
        ) : (
          repoPages.pageItems.map(repo => (
            <div key={repo._id} className="repo-card">
              <div className="repo-header">
                <div className="repo-info">
                  <h3 className="repo-name">
                    <a 
                      href={repo.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="repo-link"
                    >
                      {repo.fullName}
                    </a>
                    {repo.private && <span className="repo-badge private">Private</span>}
                    {!repo.private && <span className="repo-badge public">Public</span>}
                    {repo.userHasAccess && (
                      <span className="repo-badge public" title={`You have ${repo.userPermission} access`}>
                        You have {repo.userPermission}
                      </span>
                    )}
                    {repo.requiresMultiApproval && (
                      <span className="repo-badge multi-approval" title="Requires repo owner + admin approval">Multi-step approval</span>
                    )}
                  </h3>
                  {repo.description && (
                    <p className="repo-description">{repo.description}</p>
                  )}
                </div>
              </div>
              <div className="repo-footer">
                <div className="repo-stats">
                  <span className="repo-stat">
                    {repo.userHasAccess ? `Your access: ${repo.userPermission}` : 'No access yet — you can request it'}
                  </span>
                  {repo.accessExpiresAt && (
                    <span className="repo-stat">Expires {formatAccessExpiry(repo.accessExpiresAt)}</span>
                  )}
                </div>
                <div className="repo-actions">
                  <button
                    type="button"
                    className={`favorite-btn ${favorites.has(repo._id) ? 'is-fav' : ''}`}
                    title={favorites.has(repo._id) ? 'Unfavorite' : 'Favorite'}
                    onClick={() => handleToggleFavorite(repo._id)}
                  >
                    {favorites.has(repo._id) ? '★' : '☆'}
                  </button>
                  {user?.role === 'admin' && (
                    <button
                      type="button"
                      className={`btn btn-sm ${repo.requiresMultiApproval ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => toggleMultiApproval(repo)}
                      title="Toggle multi-step approval (repo owner then admin)"
                    >
                      {repo.requiresMultiApproval ? '✓ Multi-step' : 'Multi-step off'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => openRequestModal(repo)}
                  >
                    {repo.userHasAccess ? 'Request more access' : 'Request access'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(`/repositories/${repo.owner}/${repo.name}`)}
                  >
                    {user?.role === 'admin' ? 'Manage Access' : 'View'}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {!loadingRepos && repositories.length > 0 && (
        <Pagination
          page={repoPages.page}
          totalPages={repoPages.totalPages}
          total={repoPages.total}
          pageSize={repoPages.pageSize}
          onPageChange={repoPages.setPage}
          label="repositories"
        />
      )}

      {requestModalRepo && (
        <div className="modal-overlay" onClick={closeRequestModal}>
          <div className="modal request-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Request access to {requestModalRepo.fullName}</h3>
              <button type="button" className="modal-close" onClick={closeRequestModal} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleRequestAccess} className="modal-body">
              <p className="request-modal-hint approval-hint">{getApprovalHintText(requestModalRepo)}</p>
              <div className="form-group">
                <label className="form-label">Your GitHub username</label>
                <input
                  type="text"
                  className="form-input"
                  value={requestGitHubUsername}
                  onChange={e => setRequestGitHubUsername(e.target.value)}
                  placeholder="e.g. jane-doe"
                  required
                />
                <p className="form-hint">Access will be granted to this GitHub account.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Permission</label>
                <select
                  className="form-select"
                  value={requestPermission}
                  onChange={e => setRequestPermission(e.target.value)}
                >
                  <option value="read">Read</option>
                  <option value="write">Write</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Service desk request number</label>
                <input
                  type="text"
                  className="form-input"
                  value={serviceDeskRequestNumber}
                  onChange={(e) => setServiceDeskRequestNumber(e.target.value)}
                  placeholder="e.g. A1B2C3"
                  pattern="(#?[A-Fa-f0-9]{6}|[A-Fa-f0-9]{24})"
                  required
                />
                <p className="form-hint">The 6-character ticket code from the service desk email, or the full request id. The same ticket can be used to request more than one repository.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Business justification (required)</label>
                <textarea
                  className="form-input"
                  value={requestReason}
                  onChange={e => setRequestReason(e.target.value)}
                  rows={3}
                  placeholder="Explain the business need for this repository access (min. 10 characters)"
                  required
                  minLength={10}
                />
                <p className="form-hint">Approvers will review this justification before granting access.</p>
              </div>
              {requestError && <div className="alert alert-error">{requestError}</div>}
              {requestSuccess && (
                <div className="alert alert-success">
                  Request submitted. {getApprovalHintText(requestModalRepo)}
                </div>
              )}
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={requestSubmitting}>
                  {requestSubmitting ? 'Submitting…' : 'Submit request'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeRequestModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateRepo && (
        <div className="modal-overlay" onClick={() => setShowCreateRepo(false)}>
          <div className="modal request-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create repository on GitHub</h3>
              <button type="button" className="modal-close" onClick={() => setShowCreateRepo(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleCreateRepository} className="modal-body">
              <p className="request-modal-hint">
                The repository is created on GitHub and added to this portal immediately.
              </p>
              <div className="form-group">
                <label className="form-label">Repository name</label>
                <input
                  className="form-input"
                  value={createRepoForm.name}
                  onChange={(e) => setCreateRepoForm({ ...createRepoForm, name: e.target.value })}
                  placeholder="my-service"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  className="form-input"
                  value={createRepoForm.description}
                  onChange={(e) => setCreateRepoForm({ ...createRepoForm, description: e.target.value })}
                  placeholder="What this repository is for"
                />
              </div>
              <div className="form-group">
                <label className="form-label">GitHub organization</label>
                <input
                  className="form-input"
                  value={createRepoForm.org}
                  onChange={(e) => setCreateRepoForm({ ...createRepoForm, org: e.target.value })}
                  placeholder="Leave blank for the company GitHub user account"
                />
                <p className="form-hint">Leave this blank to create the repository on the connected company user. Fill it in only when the owner is a GitHub organization.</p>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={createRepoForm.private}
                    onChange={(e) => setCreateRepoForm({ ...createRepoForm, private: e.target.checked })}
                  />{' '}
                  Private repository
                </label>
              </div>
              {createRepoError && <div className="alert alert-error">{createRepoError}</div>}
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={creatingRepo}>
                  {creatingRepo ? 'Creating…' : 'Create on GitHub'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateRepo(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRequestByNameModal && (
        <div className="modal-overlay" onClick={closeRequestByNameModal}>
          <div className="modal request-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Request access by repository name</h3>
              <button type="button" className="modal-close" onClick={closeRequestByNameModal} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleRequestByName} className="modal-body">
              <p className="request-modal-hint">
                Enter the GitHub repository in <strong>owner/repo</strong> format. The repo must be registered in this portal.
              </p>
              <div className="form-group">
                <label className="form-label">Repository name</label>
                <input
                  type="text"
                  className="form-input"
                  value={requestByNameFullName}
                  onChange={e => setRequestByNameFullName(e.target.value)}
                  placeholder="e.g. my-org/my-private-repo"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Your GitHub username</label>
                <input
                  type="text"
                  className="form-input"
                  value={requestGitHubUsername}
                  onChange={e => setRequestGitHubUsername(e.target.value)}
                  placeholder="e.g. jane-doe"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Permission</label>
                <select
                  className="form-select"
                  value={requestByNamePermission}
                  onChange={e => setRequestByNamePermission(e.target.value)}
                >
                  <option value="read">Read</option>
                  <option value="write">Write</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Service desk request number</label>
                <input
                  type="text"
                  className="form-input"
                  value={requestByNameTicket}
                  onChange={(e) => setRequestByNameTicket(e.target.value)}
                  placeholder="e.g. A1B2C3"
                  pattern="(#?[A-Fa-f0-9]{6}|[A-Fa-f0-9]{24})"
                  required
                />
                <p className="form-hint">The 6-character ticket code from the service desk email, or the full request id. The same ticket can be used to request more than one repository.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Business justification (required)</label>
                <textarea
                  className="form-input"
                  value={requestByNameReason}
                  onChange={e => setRequestByNameReason(e.target.value)}
                  rows={3}
                  placeholder="Explain the business need for this repository access (min. 10 characters)"
                  required
                  minLength={10}
                />
                <p className="form-hint">Approvers will review this justification before granting access.</p>
              </div>
              <p className="request-modal-hint approval-hint">
                A manager, repo owner, or portal admin can approve. Multi-step repos require manager/owner then admin.
              </p>
              {requestByNameError && <div className="alert alert-error">{requestByNameError}</div>}
              {requestByNameSuccess && (
                <div className="alert alert-success">Request submitted successfully.</div>
              )}
              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={requestByNameSubmitting}>
                  {requestByNameSubmitting ? 'Submitting…' : 'Submit request'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeRequestByNameModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Repositories;
